import { Callout, Level2InnerPageLayout, noOp, type PageAction } from "@octopusdeploy/design-system-components";
import { useAggregateAPIOperationStatus, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, FeedResource, GetTeamsLookupBffResponseTeam, GetTemplatedProjectParametersResponse, GitRefResource, ProjectResource, ProjectSummaryResource, PropertyValueResource, Repository, ValidateGitRefV2Response, VariableSetResource, WorkerPoolResource, PlatformHubVersionControlSettingsResource, } from "@octopusdeploy/octopus-server-client";
import { HasVariablesInGit, mapFromEnvironmentResourceToEnvironmentV2Resource, Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PageHeaderBranchSelector } from "~/areas/projects/components/BranchSelector/PageHeaderBranchSelector";
import { toTemplatedProjectProcessIdentifier } from "~/areas/projects/components/Process/types";
import { type LoadedLibraryVariableSets, loadLibraryVariableSetVariables } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import { VcsError } from "~/areas/projects/components/VersionControl/VcsError";
import { type ProjectContextProps, useProjectContext } from "~/areas/projects/context";
import { repository } from "~/clientInstance";
import { ScriptActionContext } from "~/components/Actions/script/ScriptActionContext";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import { Form, useForm } from "~/components/form/Form/Form";
import { ProjectTemplateParameterValues } from "./ProjectTemplateParameterValues";
interface TemplatedProjectParametersModel {
    readonly parameterValues: Record<string, PropertyValueResource | undefined>;
}
export interface TemplatedProjectParametersLoaderData {
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    initialValidationResult: ValidateGitRefV2Response | undefined;
}
export interface TemplatedProjectParametersPageProps {
    loaderData: TemplatedProjectParametersLoaderData;
}
export async function templatedProjectParametersPageLoader(repository: Repository, projectContext: Promise<ProjectContextProps>): Promise<TemplatedProjectParametersLoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!persistenceSettings.Url) {
        return {
            persistenceSettings,
            initialValidationResult: undefined,
        };
    }
    const platformHubGitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    const initialValidationResult = await repository.PlatformHub.validateGitRef(platformHubGitRef);
    return {
        persistenceSettings,
        initialValidationResult,
    };
}
export function TemplatedProjectParameters({ loaderData }: TemplatedProjectParametersPageProps) {
    const projectContext = useProjectContext();
    const { model: project, gitRef, gitRefValidationError } = projectContext.state;
    return <TemplatedProjectParametersDataLoader project={project} gitRef={gitRef} gitRefValidationError={gitRefValidationError} loaderData={loaderData} projectContext={projectContext}/>;
}
function TemplatedProjectParametersDataLoader({ project, gitRef, gitRefValidationError, loaderData, projectContext, }: {
    project: ProjectResource;
    gitRef: GitRefResource | undefined;
    gitRefValidationError: ValidateGitRefV2Response | undefined;
    loaderData: TemplatedProjectParametersLoaderData;
    projectContext: ProjectContextProps;
}) {
    const projectId = project.Id;
    const isGitProject = HasVariablesInGit(project?.PersistenceSettings);
    const gitRefCanonicalName = gitRef?.CanonicalName;
    const lastFetchRef = React.useRef<{
        gitRef?: string;
    }>({ gitRef: undefined });
    React.useEffect(() => {
        lastFetchRef.current = { gitRef: undefined };
    }, [projectId]);
    const { result: templateParameters } = useQuery(async () => {
        if (!projectId)
            return null;
        if (isGitProject) {
            // If git ref is missing or unchanged, we avoid re-calling the api
            // This handles a condition where updating the project context gitRef triggers changes unexpectedly
            if (!gitRefCanonicalName || lastFetchRef.current.gitRef === gitRefCanonicalName) {
                return null;
            }
            const result = await repository.TemplatedProjects.getTemplatedProjectParametersInGit(projectId, gitRefCanonicalName);
            lastFetchRef.current = { gitRef: gitRefCanonicalName };
            return result;
        }
        else {
            const result = await repository.TemplatedProjects.getTemplatedProjectParameters(projectId);
            lastFetchRef.current = { gitRef: undefined };
            return result;
        }
    }, [projectId, isGitProject, gitRefCanonicalName], "Get template parameters");
    if (!templateParameters) {
        return <SkeletonLoadingLayout errors={[]}/>;
    }
    return <TemplatedProjectParametersWithData templateParameters={templateParameters} project={project} gitRef={gitRef} gitRefValidationError={gitRefValidationError} loaderData={loaderData} projectContext={projectContext}/>;
}
function TemplatedProjectParametersWithData({ templateParameters, project, gitRef, gitRefValidationError, loaderData, projectContext, }: {
    templateParameters: GetTemplatedProjectParametersResponse;
    project: ProjectResource;
    gitRef: GitRefResource | undefined;
    gitRefValidationError: ValidateGitRefV2Response | undefined;
    loaderData: TemplatedProjectParametersLoaderData;
    projectContext: ProjectContextProps;
}) {
    const projectId = project.Id;
    const spaceNavigation = useSpaceAwareNavigation();
    const { search: urlSearch } = useLocation();
    const navigateToGitRef = (gitRef: string) => {
        const newUrl = links.branchTemplateParametersPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }).resolveWithSpaceId(project.SpaceId);
        const fullUrl = `${newUrl}${urlSearch}`;
        spaceNavigation.navigate(fullUrl);
    };
    const { result: environmentsResponse } = useQuery(() => repository.Environments.all(), [], "Get environments");
    const { result: workerPoolsCollection } = useQuery(() => repository.WorkerPools.list(), [], "Get worker pools");
    const { result: machineRolesCollection } = useQuery(() => repository.MachineRoles.all(), [], "Get machine roles");
    const { result: teamsResponse } = useQuery(() => repository.Teams.lookupBff(), [], "Get teams");
    const { result: channelsCollection } = useQuery(() => repository.Channels.list(), [], "Get channels");
    const { result: projectsCollection } = useQuery(() => repository.Projects.summaries(), [], "Get projects");
    const { result: feedsCollection, refetch: refetchFeeds } = useQuery(() => repository.Feeds.all(), [], "Get feeds");
    const { result: projectVariableSet, refetch: refetchProjectVariables } = useQuery(() => projectContext.state.projectContextRepository.Variables.get(), [projectContext.state.projectContextRepository.Variables], "Get project variables");
    const { result: sensitiveVariables } = useQuery(async () => {
        if (!projectId || !project?.PersistenceSettings || !HasVariablesInGit(project.PersistenceSettings)) {
            return null;
        }
        return await repository.ProjectVariables.getSensitive(projectId, project.PersistenceSettings);
    }, [projectId, project?.PersistenceSettings], "Get sensitive variables for template parameters");
    const mergedProjectVariableSet = React.useMemo(() => {
        if (!sensitiveVariables) {
            return projectVariableSet;
        }
        if (sensitiveVariables) {
            if (!projectVariableSet) {
                return projectVariableSet;
            }
            return {
                ...projectVariableSet,
                Variables: (projectVariableSet.Variables ?? []).concat(sensitiveVariables.Variables),
            };
        }
        return projectVariableSet;
    }, [projectVariableSet, sensitiveVariables]);
    const platformHubGitRef = React.useMemo(() => {
        if (!loaderData.persistenceSettings.Url) {
            return undefined;
        }
        return lastAccessedPlatformHubGitRef.get(loaderData.persistenceSettings);
    }, [loaderData.persistenceSettings]);
    const { result: loadedLibraryVariableSets } = useQuery(async () => {
        if (!projectContext.state.model?.IncludedLibraryVariableSetIds?.length)
            return [];
        return await loadLibraryVariableSetVariables(projectContext.state.model.IncludedLibraryVariableSetIds);
    }, [projectContext.state.model?.IncludedLibraryVariableSetIds], "Get library variable sets");
    if (gitRefValidationError && gitRefValidationError.Type !== "Success") {
        return (<Level2InnerPageLayout header={{
                title: "Template Values",
                contextSelector: <PageHeaderBranchSelector project={project} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>,
            }} busy={false} errors={[]} fullWidth={false}>
                <VcsError project={project} error={gitRefValidationError} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>
            </Level2InnerPageLayout>);
    }
    if (!loaderData.persistenceSettings.Url) {
        return (<Callout title="Platform Hub Not Configured" type="information">
                <div>Platform Hub version control settings must be configured to use template parameters.</div>
            </Callout>);
    }
    if (!platformHubGitRef) {
        return (<Callout title="Git Reference Not Available" type="danger">
                <div>Unable to determine the git reference for Platform Hub. Please check your Platform Hub configuration.</div>
            </Callout>);
    }
    if (!templateParameters || templateParameters?.Parameters?.length < 1) {
        return (<Callout title="No Template Parameters" type="information">
                <div>This project does not have any template parameters configured.</div>
            </Callout>);
    }
    const environments = environmentsResponse?.map(mapFromEnvironmentResourceToEnvironmentV2Resource) || [];
    const workerPools = workerPoolsCollection?.Items || [];
    const machineRoles = machineRolesCollection || [];
    const teams = teamsResponse || [];
    const channels = channelsCollection?.Items || [];
    const projects = projectsCollection || [];
    const feeds = feedsCollection || [];
    if (!environmentsResponse || !workerPoolsCollection || !machineRolesCollection || !teamsResponse || !channelsCollection || !projectsCollection || !feedsCollection || loadedLibraryVariableSets === undefined || !mergedProjectVariableSet) {
        return <SkeletonLoadingLayout errors={[]}/>;
    }
    const parametersSignature = templateParameters.ParameterValues ? JSON.stringify(templateParameters.ParameterValues) : "empty";
    const formKey = `${projectId}-${parametersSignature}`;
    return (<TemplatedProjectParametersForm key={formKey} templateParameters={templateParameters} project={project} gitRef={gitRef} navigateToGitRef={navigateToGitRef} environments={environments} workerPools={workerPools} machineRoles={machineRoles} teams={teams} channels={channels} projects={projects} feeds={feeds} mergedProjectVariableSet={mergedProjectVariableSet} loadedLibraryVariableSets={loadedLibraryVariableSets || []} platformHubGitRef={platformHubGitRef} refetchProjectVariables={refetchProjectVariables} refetchFeeds={refetchFeeds}/>);
}
function TemplatedProjectParametersForm({ templateParameters, project, gitRef, navigateToGitRef, environments, workerPools, machineRoles, teams, channels, projects, feeds, mergedProjectVariableSet, loadedLibraryVariableSets, platformHubGitRef, refetchProjectVariables, refetchFeeds, }: {
    templateParameters: GetTemplatedProjectParametersResponse;
    project: ProjectResource;
    gitRef: GitRefResource | undefined;
    navigateToGitRef: (gitRef: string) => void;
    environments: ReturnType<typeof mapFromEnvironmentResourceToEnvironmentV2Resource>[];
    workerPools: WorkerPoolResource[];
    machineRoles: string[];
    teams: GetTeamsLookupBffResponseTeam[];
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    feeds: FeedResource[];
    mergedProjectVariableSet: VariableSetResource;
    loadedLibraryVariableSets: LoadedLibraryVariableSets[];
    platformHubGitRef: string | undefined;
    refetchProjectVariables: () => void;
    refetchFeeds: () => void;
}) {
    const projectId = project.Id;
    const spaceId = project.SpaceId;
    const { isInProgress, errors } = useAggregateAPIOperationStatus();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    const { result: deploymentProcess } = useQuery(async () => await repository.TemplatedProjects.getTemplatedProjectDeploymentProcess(projectId), [projectId], "Get deployment process for create release button");
    const loadVariablesKey = React.useMemo(() => {
        if (!project.ProjectTemplateDetails) {
            return "";
        }
        const processIdentifier = toTemplatedProjectProcessIdentifier(project, project.ProjectTemplateDetails);
        const gitRefName = gitRef?.CanonicalName;
        return [processIdentifier.projectId, processIdentifier.projectTemplateSlug, processIdentifier.version, gitRefName].join("_");
    }, [project, gitRef]);
    const scriptActionContext = React.useMemo(() => ({
        loadVariables: async () => {
            if (!project.ProjectTemplateDetails) {
                return [];
            }
            try {
                const processIdentifier = toTemplatedProjectProcessIdentifier(project, project.ProjectTemplateDetails);
                const gitRefName = gitRef?.CanonicalName;
                return await repository.Variables.getNamesForDeploymentProcess(processIdentifier.projectId, null, gitRefName);
            }
            catch (error) {
                console.error("Failed to load variables for templated project:", error);
                return [];
            }
        },
        key: loadVariablesKey,
    }), [project, gitRef, loadVariablesKey]);
    const { model, setModel, createSaveAction, formProps } = useForm({
        formName: "Template Parameters",
        initialModel: { parameterValues: templateParameters.ParameterValues || {} },
        onSubmit: async (repository, model: TemplatedProjectParametersModel) => {
            await saveTemplateParameters(model.parameterValues);
            return model;
        },
        submitPermission: {
            permission: Permission.ProjectEdit,
            project: project.Id,
        },
        snackbarSaveText: "Template parameters saved",
    });
    const handleParameterValueChanged = (parameterName: string, value: PropertyValueResource | undefined) => {
        setModel((prev: TemplatedProjectParametersModel) => ({
            ...prev,
            parameterValues: {
                ...prev.parameterValues,
                [parameterName]: value,
            },
        }));
    };
    const saveTemplateParameters = async (parameterValues: Record<string, PropertyValueResource | undefined>) => {
        if (!projectId || !spaceId || !templateParameters?.Parameters) {
            throw new Error("Missing project ID, space ID, or template parameters");
        }
        const valuesToSave: Record<string, PropertyValueResource> = {};
        for (const [key, value] of Object.entries(parameterValues)) {
            if (value !== undefined) {
                valuesToSave[key] = value;
            }
        }
        if (HasVariablesInGit(project.PersistenceSettings) && gitRef) {
            await repository.TemplatedProjects.saveTemplateParameterValuesInGit({
                spaceId,
                projectId,
                gitRef: gitRef.CanonicalName,
                parameterValues: valuesToSave,
            });
        }
        else {
            await repository.TemplatedProjects.saveTemplateParameterValues({
                spaceId,
                projectId,
                parameterValues: valuesToSave,
            });
        }
    };
    // Create page actions including create release button
    const pageActions: PageAction[] = [];
    const hasSteps = deploymentProcess?.Steps && deploymentProcess.Steps.length > 0;
    const createReleaseIsAllowed = hasSteps || project.IsVersionControlled;
    if (project && createReleaseIsAllowed) {
        const createReleaseButton: PageAction = {
            type: "navigate",
            buttonType: "secondary",
            label: "Create Release",
            path: links.createReleasePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }),
            onClick: () => dispatchAction("Create a release", { resource: "Create Release", action: Action.Add }),
            hasPermissions: isAllowed({ permission: Permission.ReleaseCreate, project: project.Id, tenant: "*" }),
        };
        pageActions.push(createReleaseButton);
    }
    return (
    // The insert variable button component used across deployment processes & parameter values requires the ScriptActionContext
    <ScriptActionContext.Provider value={scriptActionContext}>
            <Level2InnerPageLayout header={{
            title: "Template Values",
            primaryAction: createSaveAction(),
            pageActions: pageActions,
            contextSelector: HasVariablesInGit(project.PersistenceSettings) ? <PageHeaderBranchSelector project={project} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/> : undefined,
        }} busy={isInProgress} errors={errors.map(createErrorInfoFromError)} fullWidth={false}>
                <Form {...formProps}>
                    <ProjectTemplateParameterValues doBusyTask={async (task) => {
            await task();
            return true;
        }} onParameterValueChanged={handleParameterValueChanged} parameterValues={model.parameterValues} parameters={templateParameters.Parameters} gitRefResource={gitRef} projectVariableSet={mergedProjectVariableSet} libraryVariableSets={loadedLibraryVariableSets || []} environments={environments} analyticsStepEditorDispatch={noOp} availableWorkerPools={workerPools} machineRoles={machineRoles} teams={teams} isTenanted={project.TenantedDeploymentMode !== "Untenanted"} channels={channels} projects={projects} feeds={feeds} refreshVariables={async () => {
            refetchProjectVariables();
        }} refreshFeeds={async () => {
            refetchFeeds();
        }} platformHubDefaultGitRef={platformHubGitRef || ""}/>
                </Form>
            </Level2InnerPageLayout>
        </ScriptActionContext.Provider>);
}
